/**
 * @file user-management.tsx
 * @module app/modules/sales-management/admin/user-management/user-management
 * @description Implements the user management interface for sales-management operations.
 *
 * @author Rocket Technologies Ltd
 * @copyright 2026 Rocket Technologies Ltd. All rights reserved.
 * @since 1.1.1
 * @version 2.1.2
 */

import { useEffect, useState } from 'react';
import { Button, Card, Col, FormLabel, Row } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { KTSVG } from '../../../../../_admin/helpers';
import APICallService from '../../../../../api/apiCallService';
import { assistAdmin } from '../../../../../api/apiEndPoints';
import { getKey, setKey } from '../../../../../Global/history';
import Loader from '../../../../../Global/loader';
import Pagination from '../../../../../Global/pagination';
import { PAGE_LIMIT } from '../../../../../utils/constants';
import Method from '../../../../../utils/methods';
import { REPRESENTATIVE_ROLE, REPRESENTATIVE_STATUS } from '../../../../../utils/staticJSON';
import { listSalesRepresentative } from '../../../../../utils/storeString';
import { useDebounce } from '../../../../../utils/useDebounce';
import { CustomSelect } from '../../../../custom/Select/CustomSelect';
import ThreeDotMenu from '../../../../../_admin/assets/media/svg_uTrade/three-dot.svg';
import { CustomSelectTable } from '../../../../custom/Select/CustomSelectTable';
import AddSalesUserModal from '../../../../modals/add-sales-user-modal';
import { success } from '../../../../../Global/toast';
import { masterToast } from '../../../../../utils/toast';
const UserManagement = () => {
  const navigate = useNavigate();
  const [salesRepresentatives, setSalesRepresentatives] = useState<any>([]);
  const [totalRecords, setTotalRecords] = useState<number>(0);
  const [page, setPage] = useState<any>(getKey(listSalesRepresentative.page) || 1);
  const [pageLimit, setPageLimit] = useState<any>(
    getKey(listSalesRepresentative.limit) || PAGE_LIMIT
  );
  const [search, setSearch] = useState<any>(getKey(listSalesRepresentative.search) || '');
  const [status, setStatus] = useState<any>(
    getKey(listSalesRepresentative.status)
      ? Number(getKey(listSalesRepresentative.status))
      : undefined
  );
  const [role, setRole] = useState<any>(
    getKey(listSalesRepresentative.role)
      ? Number(getKey(listSalesRepresentative.role))
      : undefined
  );
  const [loading, setLoading] = useState<boolean>(false);
  const [showAddModal, setShowAddModal] = useState<boolean>(false);
  const [menuId, setMenuId] = useState<number>(-1);
  const [showMenu, setShowMenu] = useState<boolean>(false);
  const [selectedUser, setSelectedUser] = useState<any>(undefined);

  const menuOptions = [
    {
      label: (
        <button className="btn btn-link fs-14 fw-600 text-black ms-3 p-4">
          Edit
        </button>
      ),
      value: 1,
    }
  ];

  const onMenuOpen = (id: any) => {
    setMenuId(id);
    setShowMenu(true);
  };

  const onMenuClose = () => {
    setMenuId(-1);
    setShowMenu(false);
  };

  const openMenuOnClick = (id: any) => {
    setMenuId(id);
    setShowMenu(true);
  };

  const handleOption = (event: any, data: any) => {
    if (event.value === 1) {
      setSelectedUser(data);
      setShowAddModal(true);
    }
  };

  const handleToggleActivation = async (user: any, index: number) => {
    const apiService = new APICallService(
      assistAdmin.status,
      {
        active: `${user.status === 'Active' ? "false" : "true"}`,
      },
      { id: user._id },
      "",
      "",
      "",
      "",
      ""
    );
    const response = await apiService.callAPI();
    if (response) {
      if (user.status === 'Active') {
        success(masterToast.userDeactivated);
      } else {
        success(masterToast.userActivated);
      }
      const temp: any = [...salesRepresentatives];
      temp[index].status = user.status === 'Active' ? 'Inactive' : 'Active';
      setSalesRepresentatives(temp);
    }
  };

  useEffect(() => {
    fetchSalesUsers(page, pageLimit, search, status, role);
  }, []);


  const fetchSalesUsers = async (pageNo: number, limit: number, search: string, status: number, role: number) => {
    setLoading(true);
    let params: any = {
      pageNo: pageNo,
      limit: limit,
      sortKey: "createdAt",
      sortOrder: -1,
      needCount: true,
      searchTerm: search,
    };
    if (status !== undefined) {
      params.status = status;
    }
    if (role !== undefined) {
      params["role[0]"] = role;
    }
    let apiService = new APICallService(
      assistAdmin.list,
      params,
      "",
      "",
      "",
      "",
      "",
      ""
    );
    let response = await apiService.callAPI();
    if (response.records) {
      setTotalRecords(response.total);
      const mappedRecords = response.records.map((val: any) => {
        return {
          ...val,
          status: val.active ? "Active" : "Inactive"
        };
      });
      setSalesRepresentatives(mappedRecords);
    } else {
      setTotalRecords(0);
      setSalesRepresentatives([])
    }
    setLoading(false);
  };
  const debounce = useDebounce(fetchSalesUsers, 300);
  const handleSearch = async (value: string) => {
    setLoading(true);
    setSearch(value);
    setPage(1);
    setKey(listSalesRepresentative.page, 1);
    setKey(listSalesRepresentative.search, value, false);
    await debounce(1, pageLimit, value, status, role);
  };
  const handleStatusFilter = async (value: number) => {
    setStatus(value);
    setKey(listSalesRepresentative.status, value);
    setKey(listSalesRepresentative.page, 1);
    setLoading(true);
    setPage(1);
    await fetchSalesUsers(
      1,
      pageLimit,
      search,
      value,
      role
    );
    setLoading(false);
  };
  const handleRoleFilter = async (value: number) => {
    setRole(value);
    setKey(listSalesRepresentative.role, value);
    setKey(listSalesRepresentative.page, 1);
    setLoading(true);
    setPage(1);
    await fetchSalesUsers(
      1,
      pageLimit,
      search,
      status,
      value
    );
    setLoading(false);
  };
  const handleCurrentPage = async (val: number) => {
    setPage(val);
    setKey(listSalesRepresentative.page, val);
    await fetchSalesUsers(val, pageLimit, search, status, role);
  };
  const handleNextPage = async (val: number) => {
    setPage(val + 1);
    setKey(listSalesRepresentative.page, val + 1);
    await fetchSalesUsers(val + 1, pageLimit, search, status, role);
  };
  const handlePreviousPage = async (val: number) => {
    setPage(val - 1);
    setKey(listSalesRepresentative.page, val - 1);
    await fetchSalesUsers(val - 1, pageLimit, search, status, role);
  };
  const handlePageLimit = async (event: any) => {
    setPage(1);
    setPageLimit(parseInt(event.target.value));
    setKey(listSalesRepresentative.page, 1);
    setKey(listSalesRepresentative.limit, parseInt(event.target.value));
    await fetchSalesUsers(1, parseInt(event.target.value), search, status, role);
  };
  const isFiltered =
    search !== '' ||
    (status !== undefined && status !== null) ||
    (role !== undefined && role !== null);

  return (
    <>
      <div className="p-5">
        <Row className="align-items-center mb-5">
          <Col sm>
            <h1 className="fs-22 fw-bolder mb-0">User Management</h1>
          </Col>
          {loading || (salesRepresentatives && salesRepresentatives.length > 0) || isFiltered ? (
            <Col sm="auto">
              <div className="border border-r10px w-fit-content text-white">
                <button className="uw-btn uw-btn-primary uw-btn-lg btn btn-primary btn-lg fs-16 fw-600" onClick={() => setShowAddModal(true)}>Add new User</button>
              </div>
            </Col>
          ) : (
            <>
              <Col lg={12} className="mt-5">
                <div className="border border-r10px p-md-9 p-7">
                  <h2 className="fs-22 fw-bolder">
                    Start adding Users!
                  </h2>
                  <p className="fs-18 fw-500">
                    You can add Users and manage them.
                  </p>
                  <div className="mt-5"></div>
                  <button className="uw-btn uw-btn-primary uw-btn-lg btn btn-primary btn-lg fs-16 fw-600" onClick={() => setShowAddModal(true)}>
                    Add new User
                  </button>
                </div>
              </Col>
            </>
          )}
        </Row>
        <Row>
          <Col xs={12}>
            {loading || salesRepresentatives.length > 0 || isFiltered ? (
              <Card className="bg-light border mb-7">
                <Card.Body className="px-7">
                  <Row className="align-items-center gy-5">
                    <Col sm={12} md={6} lg={4}>
                      <FormLabel className="fs-16 fw-500">
                        Search by name, email, or role
                      </FormLabel>
                      <div className="position-relative mb-1">
                        <KTSVG
                          path="/media/icons/duotune/general/gen021.svg"
                          className="svg-icon-3 svg-icon-gray-500 position-absolute top-50 translate-middle ps-13"
                        />
                        <input
                          type="text"
                          id="kt_filter_search"
                          className="form-control form-control-custom borde-r8px bg-white ps-11"
                          placeholder="Search"
                          value={search}
                          onChange={(event: any) => {
                            handleSearch(event.target.value.trimStart());
                          }}
                        />
                      </div>
                    </Col>
                    <Col sm={12} md={6} lg={4}>
                      <FormLabel className="fs-16 fw-500">
                        Filter by status
                      </FormLabel>
                      <div>
                        <CustomSelect
                          backgroundColor="#ffff"
                          default={REPRESENTATIVE_STATUS.find(
                            (option) => option?.value == status
                          )}
                          options={REPRESENTATIVE_STATUS}
                          onChange={(event: any) =>
                            handleStatusFilter(event ? event.value : undefined)
                          }
                          isSearchable={false}
                          isMulti={false}
                          isClearable={true}
                        />
                      </div>
                    </Col>
                    <Col sm={12} md={6} lg={4}>
                      <FormLabel className="fs-16 fw-500">
                        Filter by Role
                      </FormLabel>
                      <div>
                        <CustomSelect
                          backgroundColor="#ffff"
                          default={REPRESENTATIVE_ROLE.find(
                            (option) => option?.value == role
                          )}
                          options={REPRESENTATIVE_ROLE}
                          onChange={(event: any) =>
                            handleRoleFilter(event ? event.value : undefined)
                          }
                          isSearchable={false}
                          isMulti={false}
                          isClearable={true}
                        />
                      </div>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>
            ) : (
              <></>
            )}
          </Col>
          <Col lg={12}>
            {loading ? (
              <div className="d-flex justify-content-center m-4 h-200px">
                <Loader loading={loading} />
              </div>
            ) : (
              <>
                {salesRepresentatives.length > 0 || isFiltered ? (
                  <>
                    <Card className="border border-r10px">
                      <Card.Body className="p-0">
                        <div className="table-responsive">
                          <table className="table table-rounded table-row-bordered align-middle gs-7 gy-6 mb-0">
                            <thead>
                              <tr className="fw-bold fs-16 fw-600 text-dark border-bottom align-middle">
                                <th className="min-w-md-175px min-w-150px">
                                  User name
                                </th>
                                <th className="min-w-125px">
                                  Assigned <br /> Customers
                                </th>
                                <th className="min-w-md-125px text-center">
                                  Role
                                </th>
                                <th className="min-w-md-125px text-center">
                                  Status
                                </th>
                                <th className="min-w-md-400px text-end"></th>
                              </tr>
                            </thead>
                            <tbody>
                              {salesRepresentatives.length > 0 ? (
                                salesRepresentatives.map((val: any, index: number) => {
                                  return (
                                    <tr key={val.id}>
                                      <td className="fs-15 fw-500">
                                        <div>{val.name}</div>
                                        <div className="text-muted">{val.email}</div>
                                      </td>
                                      <td className="fs-15 fw-500">
                                        {val?.currentSalesTarget?.totalAssignedCustomers ? <><div>{val?.currentSalesTarget?.totalAssignedCustomers} Customers</div>
                                          <div className="text-muted fs-14">{val?.currentSalesTarget?.totalOrders ? val?.currentSalesTarget?.totalOrders : 0} Orders</div></> : "NA"}
                                      </td>
                                      <td className="fs-15 fw-500 text-nowrap">
                                        <div className="d-flex justify-content-center">
                                          <span className="badge badge-light custom-badge">
                                            {Method.getSalesRole(val.role[0]?.type)}
                                          </span>
                                        </div>
                                      </td>
                                      <td className="fs-15 fw-500">
                                        <div className="d-flex justify-content-center">
                                          <label className="form-check form-switch form-check-custom form-check-solid ">
                                            <input
                                              className="form-check-input form-check-success w-50px h-30px "
                                              type="checkbox"
                                              name="status"
                                              disabled={
                                                val.userType == 1
                                              }
                                              onChange={() =>
                                                handleToggleActivation(
                                                  val,
                                                  index
                                                )
                                              }
                                              defaultChecked={
                                                val.status === 'Active'
                                              }
                                              checked={val.status === 'Active'}
                                            />
                                          </label>
                                        </div>
                                      </td>
                                      <td className="text-end">
                                        <div className="d-flex justify-content-end align-items-center gap-3 text-nowrap">
                                          <Button
                                            className="uw-btn uw-btn-primary fs-14 fw-600"
                                            onClick={() => {
                                              let currentScrollY = window.scrollY;
                                              setKey(listSalesRepresentative.scrollPosition, currentScrollY);
                                              navigate("/sales-management/admin/user-management/customer-assignment", {
                                                state: { id: val._id, districts: val.districts || [], activationManager: val.role[0]?.type === 6 ? true : false }
                                              });
                                            }}
                                            disabled={val.role[0]?.type === 2 || val.role[0]?.type === 7}
                                          >
                                            Customer Assignment
                                          </Button>
                                          <Button
                                            className="uw-btn uw-btn-primary fs-14 fw-600"
                                            onClick={() => {
                                              let currentScrollY = window.scrollY;
                                              setKey(listSalesRepresentative.scrollPosition, currentScrollY);
                                              navigate("/sales-management/admin/user-management/user-details", {
                                                state: { id: val._id, roleType: val.role[0]?.type }
                                              });
                                            }}
                                          >
                                            View details
                                          </Button>
                                          <div className="my-0">
                                            <CustomSelectTable
                                              marginLeft={"-110px"}
                                              width={"auto"}
                                              placeholder={
                                                <img
                                                  className="img-fluid"
                                                  width={23}
                                                  height={7}
                                                  src={ThreeDotMenu}
                                                  alt=""
                                                />
                                              }
                                              options={menuOptions}
                                              backgroundColor="white"
                                              show={showMenu && index === menuId}
                                              onMenuClose={onMenuClose}
                                              openMenuOnClick={() => openMenuOnClick(index)}
                                              onMenuOpen={() => onMenuOpen(index)}
                                              onChange={(event: any) => handleOption(event, val)}
                                            />
                                          </div>
                                        </div>
                                      </td>
                                    </tr>
                                  );
                                })
                              ) : (
                                <tr>
                                  <td colSpan={5} className="fs-15 fw-600 text-center">
                                    No Data Found
                                  </td>
                                </tr>
                              )}
                            </tbody>
                          </table>
                        </div>
                      </Card.Body>
                    </Card>
                    {!loading && salesRepresentatives.length > 0 ? (
                      <Pagination
                        totalRecords={totalRecords}
                        currentPage={page}
                        handleCurrentPage={handleCurrentPage}
                        handleNextPage={handleNextPage}
                        handlePreviousPage={handlePreviousPage}
                        handlePageLimit={handlePageLimit}
                        pageLimit={pageLimit}
                      />
                    ) : (
                      <></>
                    )}
                  </>
                ) : (
                  <></>
                )}
              </>
            )}
          </Col>
        </Row>
        <AddSalesUserModal
          show={showAddModal}
          onHide={() => {
            setShowAddModal(false);
            setSelectedUser(undefined);
          }}
          editData={selectedUser}
          onSave={() => {
            fetchSalesUsers(page, pageLimit, search, status, role);
            setSelectedUser(undefined);
          }}
        />
      </div>
    </>
  );
};
export default UserManagement;
